import { createContext, useCallback, useContext, useRef, useState } from "react";
import { GetPatientDetails } from "../../pages/patientDetail/Service";
import toast from "react-hot-toast";

const PatientDataContext = createContext(null);

export function PatientDataProvider({ children }) {
  const [patientData, setPatientData] = useState(null);
  const [loading, setLoading]         = useState(false);

  // Cache keyed by "caseId::companyId"
  const cacheRef        = useRef({});          // { [cacheKey]: data }
  const fetchPromisesRef = useRef({});   // { [cacheKey]: Promise }
  const activeCacheKey  = useRef(null);  // tracks what's currently loaded into state

  const getCacheKey = (caseId, companyId) => `${caseId}::${companyId}`;

  /**
   * Fetches patient data once per (caseId, companyId) pair and caches it.
   * Subsequent calls with the same pair return the cached value immediately.
   *
   * @param {string}  caseId
   * @param {string}  companyId
   * @param {boolean} force – set true to bypass cache (e.g. after save)
   */
  const fetchPatientData = useCallback(async (caseId, companyId, force = false) => {
    if (!caseId || !companyId) return null;

    const cacheKey = getCacheKey(caseId, companyId);

    // Return cached data if the (caseId, companyId) pair is already cached
    if (!force && cacheRef.current[cacheKey]) {
      // Sync state if a different pair was loaded before
      if (activeCacheKey.current !== cacheKey) {
        setPatientData(cacheRef.current[cacheKey]);
        activeCacheKey.current = cacheKey;
      }
      return cacheRef.current[cacheKey];
    }

    if (fetchPromisesRef.current[cacheKey]) {
      return fetchPromisesRef.current[cacheKey];
    }

    const fetchPromise = (async () => {
      setLoading(true);
      try {
        const response = await GetPatientDetails(caseId, companyId);
        if (response?.success && response?.data) {
          cacheRef.current[cacheKey] = response.data;   // store in cache
          activeCacheKey.current     = cacheKey;
          setPatientData(response.data);
          return response.data;
        } else {
          console.error("[PatientDataContext] Failed to fetch patient data:", response?.message);
          toast.error("Something went wrong while fetching patient details.");
          return null;
        }
      } catch (err) {
        console.error("[PatientDataContext] Error fetching patient data:", err);
        toast.error("Something went wrong while fetching patient details.");
        return null;
      } finally {
        delete fetchPromisesRef.current[cacheKey];
        setLoading(false);
      }
    })();

    fetchPromisesRef.current[cacheKey] = fetchPromise;
    return fetchPromise;
  }, []);

  /** Busts the cache for this (caseId, companyId) pair and re-fetches */
  const refreshPatientData = useCallback((caseId, companyId) => {
    const cacheKey = getCacheKey(caseId, companyId);
    delete cacheRef.current[cacheKey];             // invalidate only this entry
    return fetchPatientData(caseId, companyId, true);
  }, [fetchPatientData]);

  return (
    <PatientDataContext.Provider value={{
      patientData,
      patientDataLoading: loading,
      fetchPatientData,
      refreshPatientData,
    }}>
      {children}
    </PatientDataContext.Provider>
  );
}

/** Hook for consuming patient data in any child component */
export function usePatientData() {
  const ctx = useContext(PatientDataContext);
  if (!ctx) throw new Error("usePatientData must be used inside <PatientDataProvider>");
  return ctx;
}